import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Account,
  OIDC,
  Permissions,
  User,
  TokenUser,
  getEnumName,
  pluralize,
  queries,
  service,
  useAuthConfig,
  useIsCurrentPermission,
  useUserCount,
  isUserMatch,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  MoreIcon,
  ToolsIcon,
  TrashcanIcon,
  UserAddIcon,
  UserIcon,
  notifyError,
  notifySuccess,
  Truncate,
} from "@theprelude/ds";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { Flag } from "@/components/flag/flag";
import { SignInMethod } from "@/components/sign-in-method";
import { useTable } from "@/components/table/hooks";
import { Unavailable } from "@/components/table/unavaliable";
import { VirtualTable } from "@/components/table/virtual-table";
import { useSelectedUser } from "@/components/update-user/update-user-overlay";
import { useOverlay } from "@/hooks/use-overlay";
import { usePopper } from "@/hooks/use-popper";
import { tb } from "@/lib/tracking";

export const Route = createFileRoute("/_app/account/users")({
  beforeLoad() {
    setPageTitle("Users");
  },
  component: UsersPage,
});

function UsersPage() {
  const overlay = useOverlay();
  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="flex flex-col gap-4">
        <div className={"flex min-h-[38px] items-center justify-between"}>
          <h1 className="min-h-[38px] text-2xl dark:font-light">Users</h1>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                void overlay.open("invite-user");
              }}
              icon={<UserAddIcon />}
              intent="primary"
              className={tb("invite-a-user")}
            >
              Invite a user
            </Button>
            <Flag features={["detect"]}>
              <Button
                onClick={() => {
                  void overlay.open("service-user");
                }}
                intent="secondary"
                icon={<ToolsIcon className="text-secondary-foreground" />}
              >
                Add a service user
              </Button>
            </Flag>
          </div>
        </div>
        <div className="border-b border-stroke-line" />

        <div className="flex flex-col gap-4">
          <UserCount />
          <UsersTable />
        </div>
      </div>
      <Flag features={["detect"]}>
        <div className="mt-8">
          <h2 className="mb-4 text-xl dark:font-light">Service Users</h2>
          <ServiceUsersTable />
        </div>
      </Flag>
    </div>
  );
}

function UserCount() {
  const { data: userCount } = useUserCount();

  if (userCount === undefined) {
    return null;
  }

  return (
    <p className="text-secondary-foreground">{`${userCount} ${pluralize(
      userCount,
      "user",
    )}`}</p>
  );
}

type UserTableEntry = {
  user: User;
};

type ServiceUserTableEntry = {
  user: TokenUser;
};

const columnHelper = createColumnHelper<UserTableEntry>();
const serviceColumnHelper = createColumnHelper<ServiceUserTableEntry>();

const transformTableData = (account: Account): UserTableEntry[] => {
  return account.users
    .filter((user) => user.permission !== Permissions.SUPPORT)
    .map((user) => {
      return {
        user,
      };
    });
};

const transformServiceTableData = (
  account: Account,
): ServiceUserTableEntry[] => {
  return account.token_users.map((user) => {
    return {
      user: {
        ...user,
      },
    };
  });
};

const columns = [
  columnHelper.accessor((row) => row.user.handle, {
    id: "handle",
    cell: (info) => (
      <div className="flex min-w-0 items-center gap-2">
        <UserIcon className="size-4 text-secondary-foreground" />
        <div className="flex min-w-0 flex-col">
          {info.row.original.user.name && (
            <span className="text-xs text-secondary-foreground font-medium">
              {info.row.original.user.name}
            </span>
          )}
          <Truncate text={info.getValue()}>
            <UserName user={info.row.original.user} />
          </Truncate>
        </div>
      </div>
    ),
    meta: {
      label: "User",
      widths: ["180px", "3fr "],
    },
  }),
  columnHelper.accessor((row) => row.user.permission, {
    id: "permission",
    cell: (info) => (
      <div className="flex items-center gap-2">
        <span>{getEnumName(Permissions, info.getValue())}</span>
      </div>
    ),
    meta: {
      label: "Permission",
      widths: ["150px", "1fr "],
    },
  }),

  columnHelper.accessor((row) => row.user.oidc, {
    id: "oidc",
    cell: (info) => <SignInMethod oidc={info.getValue()} />,
    meta: {
      label: "Sign in method",
      widths: ["150px", "1fr "],
    },
  }),

  columnHelper.accessor((row) => row.user.last_seen, {
    id: "last_seen",
    cell: (info) => {
      const lastSeen = info.getValue();
      if (lastSeen) {
        return <div className="flex items-center gap-2">{lastSeen}</div>;
      }
      return <Unavailable />;
    },
    meta: {
      label: "Last seen",
      widths: ["150px", "1fr "],
    },
  }),

  columnHelper.display({
    id: "actions",
    cell: (info) => <UserDropdown user={info.row.original.user} />,
    meta: {
      widths: ["50px", "50px"],
    },
    enableSorting: false,
  }),
];

const serviceColumns = [
  serviceColumnHelper.accessor((row) => row.user.name, {
    id: "name",
    cell: (info) => (
      <div className="flex min-w-0 items-center gap-2">
        <UserIcon className="size-4" />
        <div className="flex min-w-0 flex-col">
          <Truncate text={info.getValue()}>
            <span>{info.getValue()}</span>
          </Truncate>
        </div>
      </div>
    ),
    meta: {
      label: "Name",
      widths: ["180px", "2fr "],
    },
  }),

  serviceColumnHelper.accessor((row) => row.user.handle, {
    id: "handle",
    cell: (info) => (
      <Truncate text={info.getValue()}>
        <span>{info.getValue()}</span>
      </Truncate>
    ),
    meta: {
      label: "Handle",
      widths: ["180px", "2fr "],
    },
  }),
  serviceColumnHelper.accessor((row) => row.user.last_seen, {
    id: "last_seen",
    cell: (info) => {
      const lastSeen = info.getValue();
      if (lastSeen) {
        return <div className="flex items-center gap-2">{lastSeen}</div>;
      }
      return <Unavailable />;
    },
    meta: {
      label: "Last seen",
      widths: ["150px", "1fr "],
    },
  }),
  serviceColumnHelper.display({
    id: "actions",
    cell: (info) => <ServiceUserDropdown user={info.row.original.user} />,
    meta: {
      widths: ["50px", "50px"],
    },
    enableSorting: false,
  }),
];

export function UsersTable() {
  const authConfig = useAuthConfig();
  const { data: users } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: transformTableData,
  });

  const table = useTable({
    name: "users",
    data: users,
    columns,
  });

  return <VirtualTable size="large" showFilter table={table} />;
}

export function ServiceUsersTable() {
  const overlay = useOverlay();
  const authConfig = useAuthConfig();
  const { data: serviceUsers } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: transformServiceTableData,
  });

  const table = useTable({
    name: "service-users",
    data: serviceUsers,
    columns: serviceColumns,
  });

  return (
    <VirtualTable
      emptyState={
        <div className="flex items-center justify-center">
          <p>No service users found</p>

          <Button
            intent="text-link"
            onClick={() => {
              void overlay.open("service-user");
            }}
          >
            Add a service user
          </Button>
        </div>
      }
      size="large"
      table={table}
    />
  );
}

function UserName({ user }: { user: User }) {
  const authConfig = useAuthConfig();
  const { data: whoami } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (data) => data.whoami,
  });

  return (
    <span>
      {user.handle} {isUserMatch(whoami, user) && <span>(You)</span>}
    </span>
  );
}

export function DeleteUserModal(props: {
  user: string;
  oidc: OIDC;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  const authConfig = useAuthConfig();

  const deleteUser = useMutation({
    mutationFn: async (user: string) => {
      await service(authConfig).iam.deleteUser({
        handle: user,
        oidc: props.oidc ?? "",
      });
    },
    onSuccess: async () => {
      notifySuccess({
        message: "Successfully deleted user",
        dismissible: true,
      });
      props.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to delete user",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you sure you want to remove this user?</DialogTitle>
        </DialogHeader>
        <DialogBody>This will remove their access to your account</DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            intent="negative"
            loading={deleteUser.isPending}
            icon={<TrashcanIcon />}
            onClick={() => deleteUser.mutate(props.user)}
          >
            Remove this user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDropdown({ user }: { user: User }) {
  const setUserToUpdate = useSelectedUser((state) => state.setValue);
  const deleteDialog = usePopper();
  const { data: isAdmin } = useIsCurrentPermission(Permissions.ADMIN);
  const reinviteDialog = usePopper();
  const isServiceUser = user.permission === Permissions.SERVICE;
  return (
    <>
      <Dropdown>
        <DropdownTrigger asChild className="ml-auto">
          <IconButton icon={<MoreIcon />} />
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownItem disabled={!isAdmin} onSelect={deleteDialog.on}>
            Delete user
          </DropdownItem>
          <DropdownItem
            disabled={!isAdmin || user.permission === Permissions.SERVICE}
            onSelect={() => setUserToUpdate(user)}
          >
            Update user
          </DropdownItem>
          {!user.oidc && !isServiceUser && (
            <DropdownItem disabled={!isAdmin} onSelect={reinviteDialog.on}>
              Reinvite user
            </DropdownItem>
          )}
        </DropdownContent>
      </Dropdown>

      <DeleteUserModal {...deleteDialog} user={user.handle} oidc={user.oidc} />
      <ReinviteUserModal {...reinviteDialog} user={user} />
    </>
  );
}

function ReinviteUserModal(props: {
  user: User;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  const authConfig = useAuthConfig();

  const reinviteUser = useMutation({
    mutationFn: async () => {
      await service(authConfig).iam.inviteUser({
        email: props.user.handle,
        name: props.user.name,
        permission: getEnumName(Permissions, props.user.permission),
        oidc: props.user.oidc,
      });
    },
    onSuccess: async () => {
      notifySuccess({
        message: "Successfully reinvited user",
        dismissible: true,
      });
      props.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to reinvite user",
        error,
      });
    },
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to reinvite this user?
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p>
            This will send a new invitation to the user&apos;s email address.
          </p>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            intent="primary"
            loading={reinviteUser.isPending}
            onClick={() => reinviteUser.mutate()}
          >
            Reinvite user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServiceUserDropdown({ user }: { user: TokenUser }) {
  const deleteDialog = usePopper();
  const { data: isAdmin } = useIsCurrentPermission(Permissions.ADMIN);

  return (
    <>
      <Dropdown>
        <DropdownTrigger asChild className="ml-auto">
          <IconButton icon={<MoreIcon />} />
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownItem disabled={!isAdmin} onSelect={deleteDialog.on}>
            Delete user
          </DropdownItem>
        </DropdownContent>
      </Dropdown>

      <DeleteServiceUserModal {...deleteDialog} user={user.handle} />
    </>
  );
}

function DeleteServiceUserModal(props: {
  user: string;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  const authConfig = useAuthConfig();

  const deleteServiceUser = useMutation({
    mutationFn: async () => {
      await service(authConfig).iam.deleteServiceUser(props.user);
    },
    onSuccess: async () => {
      notifySuccess({
        message: "Successfully deleted service user",
        dismissible: true,
      });
      props.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to delete service user",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to delete this service user?
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p>This will remove their access to your account</p>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            intent="negative"
            loading={deleteServiceUser.isPending}
            onClick={() => deleteServiceUser.mutate()}
          >
            Delete service user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
